/**
 * Station create/edit form — same pattern as EmployeeForm (CRUD template).
 * Owner field: Super Admin — dropdown all merchants; Admin — dropdown admin's merchants; Merchant — read-only email.
 */
import * as React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormGroup from '@mui/material/FormGroup';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AddressSuggestField from '../components/AddressSuggestField';
import GoogleMapPicker from '../components/GoogleMapPicker';
import GuidePhotosField from '../components/GuidePhotosField';
import {
  getDefaultWorkTime,
  isAllDaySchedule,
  isCustomSchedule,
  isNotWorkSchedule,
  WORK_TIME_DAYS as DAYS,
} from '../utils/workTime';

const DAY_LABEL_KEYS = { monday: 'dayMon', tuesday: 'dayTue', wednesday: 'dayWed', thursday: 'dayThu', friday: 'dayFri', saturday: 'daySat', sunday: 'daySun' };

function StationForm(props) {
  const {
    formState,
    onFieldChange,
    onSubmit,
    onReset,
    submitButtonLabel,
    backButtonPath,
    ownerMode = 'super_admin',
    merchantsList = [],
    tariffsList = [],
    onAddressSelect,
    onWorkTimeChange,
    guidePhotos = [],
    onGuidePhotosChange,
    minPhotos = 3,
  } = props;

  const formValues = formState.values;
  const formErrors = formState.errors;
  const history = useHistory();
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [mapDialogOpen, setMapDialogOpen] = React.useState(false);

  const handleSubmit = React.useCallback(
    async (event) => {
      event.preventDefault();
      setIsSubmitting(true);
      try {
        await onSubmit(formValues);
      } finally {
        setIsSubmitting(false);
      }
    },
    [formValues, onSubmit],
  );

  const handleTextFieldChange = React.useCallback(
    (event) => {
      onFieldChange(event.target.name, event.target.value);
    },
    [onFieldChange],
  );

  const handleSelectChange = React.useCallback(
    (event) => {
      onFieldChange('owner', event.target.value);
    },
    [onFieldChange],
  );

  const handleTariffChange = React.useCallback(
    (event) => {
      onFieldChange('tariffPlanName', event.target.value);
    },
    [onFieldChange],
  );

  const handleWorkTimeTypeChange = React.useCallback(
    (day, type) => {
      if (!onWorkTimeChange) return;
      if (type === 'AllDay') onWorkTimeChange(day, ['AllDay']);
      else if (type === 'NotWork') onWorkTimeChange(day, ['NotWork']);
      else onWorkTimeChange(day, ['09:00', '18:00']);
    },
    [onWorkTimeChange],
  );

  const handleWorkTimeRangeChange = React.useCallback(
    (day, fromTo, value) => {
      if (!onWorkTimeChange) return;
      const workTime = formValues.workTime || getDefaultWorkTime();
      const current = workTime[day];
      const isRange = isCustomSchedule(current);
      const from = fromTo === 'from' ? value : (isRange ? current[0] : '09:00');
      const to = fromTo === 'to' ? value : (isRange ? current[1] : '18:00');
      onWorkTimeChange(day, [from, to]);
    },
    [formValues.workTime, onWorkTimeChange],
  );

  const handleNumberFieldChange = React.useCallback(
    (event) => {
      const raw = event.target.value;
      if (raw === '' || raw == null) {
        onFieldChange(event.target.name, '');
        return;
      }
      const num = Number(raw);
      onFieldChange(event.target.name, Number.isFinite(num) ? num : raw);
    },
    [onFieldChange],
  );

  const handleReset = React.useCallback(() => {
    if (onReset) onReset(formValues);
  }, [formValues, onReset]);

  const handleBack = React.useCallback(() => {
    history.push(backButtonPath ?? '/station');
  }, [history, backButtonPath]);

  const isOwnerMerchant = ownerMode === 'merchant';

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      noValidate
      autoComplete="off"
      onReset={handleReset}
      sx={{ width: '100%' }}
    >
      <FormGroup>
        <Grid container spacing={2} sx={{ mb: 2, width: '100%' }}>
          <Grid size={{ xs: 12, sm: 6 }} sx={{ display: 'flex' }}>
            <TextField
              value={formValues.stationID ?? ''}
              onChange={handleTextFieldChange}
              name="stationID"
              label={t('stationPage.stationIdLabel')}
              placeholder={t('stationPage.stationIdPlaceholder')}
              error={!!formErrors.stationID}
              helperText={formErrors.stationID ?? ' '}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }} sx={{ display: 'flex' }}>
            <TextField
              value={formValues.stationNumber ?? ''}
              onChange={handleTextFieldChange}
              name="stationNumber"
              label={t('stationPage.stationNumberLabel')}
              placeholder={t('stationPage.stationNumberPlaceholder')}
              error={!!formErrors.stationNumber}
              helperText={formErrors.stationNumber ?? ' '}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12 }} sx={{ display: 'flex' }}>
            <AddressSuggestField
              label={t('stationPage.addressLabel')}
              value={formValues.address ?? ''}
              onChange={(v) => onFieldChange('address', v)}
              onSelect={(suggestion) => {
                if (onAddressSelect) onAddressSelect(suggestion);
              }}
              error={!!formErrors.address}
              helperText={
                formErrors.address
                || ((formValues.latitude && formValues.longitude) ? ' ' : t('stationPage.coordsHint'))
              }
              placeholder={t('stationPage.addressPlaceholder')}
            />
          </Grid>
          {onAddressSelect && (
            <Grid size={{ xs: 12 }} sx={{ display: 'flex' }}>
              <Button
                variant="outlined"
                startIcon={<MapOutlinedIcon />}
                onClick={() => setMapDialogOpen(true)}
              >
                {t('stationPage.setLocationOnMap')}
              </Button>
              <Dialog
                open={mapDialogOpen}
                onClose={() => setMapDialogOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { maxHeight: '90vh' } }}
              >
                <DialogTitle>{t('stationPage.setLocationOnMapTitle')}</DialogTitle>
                <DialogContent>
                  <GoogleMapPicker
                    center={
                      formValues.latitude != null && formValues.longitude != null
                        ? {
                            lat: Number(formValues.latitude) || formValues.latitude,
                            lng: Number(formValues.longitude) || formValues.longitude,
                          }
                        : undefined
                    }
                    onSelect={(suggestion) => {
                      if (onAddressSelect) onAddressSelect(suggestion);
                      setMapDialogOpen(false);
                    }}
                    disabled={!!formErrors.address}
                  />
                </DialogContent>
              </Dialog>
            </Grid>
          )}
          <Grid size={{ xs: 12 }} sx={{ display: 'flex' }}>
            <TextField
              value={formValues.additionalAddress ?? ''}
              onChange={handleTextFieldChange}
              name="additionalAddress"
              label={t('stationPage.additionalAddressLabel')}
              error={!!formErrors.additionalAddress}
              helperText={formErrors.additionalAddress ?? ' '}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }} sx={{ display: 'flex' }}>
            <TextField
              type="number"
              value={formValues.numberSlots ?? ''}
              onChange={handleNumberFieldChange}
              name="numberSlots"
              label={t('stationPage.numberSlotsLabel')}
              inputProps={{ min: 0 }}
              error={!!formErrors.numberSlots}
              helperText={formErrors.numberSlots ?? ' '}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }} sx={{ display: 'flex' }}>
            {isOwnerMerchant ? (
              <TextField
                value={formValues.owner ?? ''}
                name="owner"
                label={t('stationPage.ownerLabel')}
                fullWidth
                disabled
                helperText={t('stationPage.ownerMerchantHelp')}
              />
            ) : (
              <FormControl fullWidth error={!!formErrors.owner}>
                <InputLabel id="station-owner-label">{t('stationPage.ownerLabel')}</InputLabel>
                <Select
                  labelId="station-owner-label"
                  name="owner"
                  value={formValues.owner ?? ''}
                  onChange={handleSelectChange}
                  label={t('stationPage.ownerLabel')}
                >
                  <MenuItem value="">
                    <em>{t('stationPage.selectMerchantOption')}</em>
                  </MenuItem>
                  {merchantsList.map((email) => (
                    <MenuItem key={email} value={email}>
                      {email}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{formErrors.owner ?? ' '}</FormHelperText>
              </FormControl>
            )}
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }} sx={{ display: 'flex' }}>
            <FormControl fullWidth error={!!formErrors.tariffPlanName}>
              <InputLabel id="station-tariff-label">{t('stationPage.tariffLabel')}</InputLabel>
              <Select
                labelId="station-tariff-label"
                name="tariffPlanName"
                value={formValues.tariffPlanName ?? ''}
                onChange={handleTariffChange}
                label={t('stationPage.tariffLabel')}
              >
                <MenuItem value="">
                  <em>{t('stationPage.selectTariffOption')}</em>
                </MenuItem>
                {tariffsList.map((tariff) => {
                  const name = typeof tariff === 'string' ? tariff : tariff?.name;
                  const id = typeof tariff === 'string' ? tariff : tariff?.id || name;
                  if (!name) return null;
                  return (
                    <MenuItem key={id} value={name}>
                      {name}
                    </MenuItem>
                  );
                })}
              </Select>
              <FormHelperText>{formErrors.tariffPlanName ?? ' '}</FormHelperText>
            </FormControl>
          </Grid>
        </Grid>
        {onWorkTimeChange && (
          <Paper variant="outlined" sx={{ mt: 3, p: 2, width: '100%' }}>
            <Typography component="span" variant="subtitle2" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              {t('stationPage.workingHours')}
            </Typography>
            <Grid container spacing={2}>
              {DAYS.map((day) => {
                const workTime = formValues.workTime || getDefaultWorkTime();
                const daySchedule = workTime[day];
                const isAllDay = isAllDaySchedule(daySchedule);
                const isNotWork = isNotWorkSchedule(daySchedule);
                const isCustom = isCustomSchedule(daySchedule);
                const type = isAllDay ? 'AllDay' : isNotWork ? 'NotWork' : isCustom ? 'Custom' : 'AllDay';
                const from = isCustom ? daySchedule[0] : '09:00';
                const to = isCustom ? daySchedule[1] : '18:00';
                const dayLabelKey = `stationPage.${DAY_LABEL_KEYS[day]}`;
                return (
                  <Grid key={day} size={{ xs: 12, sm: 6, md: 4 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="body2" sx={{ minWidth: 40 }}>{t(dayLabelKey)}</Typography>
                      <FormControl size="small" sx={{ minWidth: 130, flex: 1 }}>
                        <Select
                          value={type}
                          onChange={(e) => handleWorkTimeTypeChange(day, e.target.value)}
                          displayEmpty
                        >
                          <MenuItem value="AllDay">{t('stationPage.allDay')}</MenuItem>
                          <MenuItem value="NotWork">{t('stationPage.notWorking')}</MenuItem>
                          <MenuItem value="Custom">{t('stationPage.fromTo')}</MenuItem>
                        </Select>
                      </FormControl>
                      {type === 'Custom' && (
                        <>
                          <TextField
                            type="time"
                            size="small"
                            value={from}
                            onChange={(e) => handleWorkTimeRangeChange(day, 'from', e.target.value)}
                            inputProps={{ step: 300 }}
                            sx={{ width: 100 }}
                          />
                          <TextField
                            type="time"
                            size="small"
                            value={to}
                            onChange={(e) => handleWorkTimeRangeChange(day, 'to', e.target.value)}
                            inputProps={{ step: 300 }}
                            sx={{ width: 100 }}
                          />
                        </>
                      )}
                    </Stack>
                  </Grid>
                );
              })}
            </Grid>
            {formErrors.workTime && (
              <FormHelperText error sx={{ mt: 0.5 }}>{formErrors.workTime}</FormHelperText>
            )}
          </Paper>
        )}
        {onGuidePhotosChange && (
          <GuidePhotosField
            value={guidePhotos}
            onChange={onGuidePhotosChange}
            error={formErrors.guidePhotos}
            minPhotos={minPhotos}
          />
        )}
      </FormGroup>
      <Stack direction="row" spacing={2} justifyContent="space-between" sx={{ mt: 3 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
        >
          {t('stationPage.back')}
        </Button>
        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={isSubmitting}
        >
          {isSubmitting ? (submitButtonLabel === t('stationPage.save') ? t('stationPage.saving') : t('stationPage.creating')) : submitButtonLabel}
        </Button>
      </Stack>
    </Box>
  );
}

StationForm.propTypes = {
  backButtonPath: PropTypes.string,
  formState: PropTypes.shape({
    errors: PropTypes.object,
    values: PropTypes.shape({
      address: PropTypes.string,
      additionalAddress: PropTypes.string,
      latitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      longitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      numberSlots: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      owner: PropTypes.string,
      stationID: PropTypes.string,
      stationNumber: PropTypes.string,
      tariffPlanName: PropTypes.string,
      workTime: PropTypes.object,
    }),
  }).isRequired,
  merchantsList: PropTypes.arrayOf(PropTypes.string),
  tariffsList: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({ id: PropTypes.string, name: PropTypes.string }),
    ]),
  ),
  onFieldChange: PropTypes.func.isRequired,
  onAddressSelect: PropTypes.func,
  onWorkTimeChange: PropTypes.func,
  guidePhotos: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.oneOf(['streetView', 'entrance', 'landmark']),
      id: PropTypes.string,
      file: PropTypes.object,
      comment: PropTypes.string,
      previewUrl: PropTypes.string,
      existingUrl: PropTypes.string,
    })
  ),
  onGuidePhotosChange: PropTypes.func,
  minPhotos: PropTypes.number,
  onReset: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  ownerMode: PropTypes.oneOf(['merchant', 'admin', 'super_admin']),
  submitButtonLabel: PropTypes.string.isRequired,
};

export default StationForm;
